import { getPriceVariation } from "$/api/market-monitoring/other-markets.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import { Dropdown } from "$/lib/components/molecules";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useScreen } from "usehooks-ts";

const PriceVariationChart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const { height } = useScreen();

  const currentYear = new Date().getFullYear();
  const YEARS_OPTIONS = Array.from(new Array(currentYear - 2020), (_, i) => ({
    value: `${currentYear - i}`,
    label: `${currentYear - i}`,
  })).reverse();

  const [year, setYear] = useState<string>("2025");

  const [COLORS, ORDER] = useMemo(() => {
    const elec = `Elec France Cal+1 - ${+year + 1}`;
    const gas = `Gaz PEG Cal+1 - ${+year + 1}`;

    const colors: Record<string, string> = {
      Coal: "#c38649",
      [`${elec}`]: "#b7dadb",
      [`${gas}`]: "#b0cc7f",
      Brent: "#2f3231",
      EUA: "#c7c9c9",
    };

    return [colors, ["Brent", "EUA", "Coal", elec, gas]];
  }, [year]);

  const { data, isLoading } = useQuery({
    queryKey: ["get-other-markets-price-variation", year],
    queryFn: () => getPriceVariation({ year }),
    gcTime: 0,
  });

  const keys = useMemo(
    () => getArrayKeys(data ?? [], "date", ORDER),
    [ORDER, data],
  );

  const colors: Record<string, string> = keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: Object.entries(COLORS).find(([k]) => key.includes(k))?.[1],
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    setGraphVisibility(
      keys.map((key) => ({
        name: key,
        isVisible: true,
        color: Object.entries(COLORS).find(([k]) => key.includes(k))?.[1],
      })),
    );
  }, [COLORS, keys]);

  return (
    <DisplayBox
      title="Indices de prix Base 100 par rapport au 1er janvier d’une année donnée"
      Icon={LineChartIcon}
      theme="orange"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="py-0" style={{ height: height * 0.75 }}>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <LineGraph
            data={data ?? []}
            xAxisDataKey="date"
            graphVisibility={graphVisibility}
            colors={colors}
            theme="orange"
            keys={keys}
            unit="%"
            yAxisLabel="Variation (%)"
            leftMargin={-20}
            hasCustomXAxisDomain={false}
          />
        )}

        <FiltersDisplayBox>
          <Dropdown<string>
            options={YEARS_OPTIONS}
            placeholder="Année"
            onChange={(option) => {
              if (!option) return;
              setYear(option.value);
            }}
            value={year}
            containerClassName="w-[150px]"
          />

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default PriceVariationChart;
